import { useState } from "react";
import { PageHeader } from "@/components/PageHeader";
import { Section, Eyebrow } from "@/components/kit";
import { Reveal } from "@/components/motion";
import { PriceCards } from "@/components/PriceCards";
import { ContactForm } from "@/components/ContactForm";
import { ActLayers } from "@/components/ActLayers";
import { RoiGrid } from "@/components/RoiGrid";
import { DuoBanner } from "@/components/DuoBanner";
import { OnboardingCTA } from "@/components/OnboardingCTA";
import { CommercialInventory } from "@/components/CommercialInventory";
import { BRAND_ACTS, BRAND_TIERS, BRAND_ROI, IMG } from "@/data/content";

export default function Marcas() {
  const [prefill, setPrefill] = useState(null);
  const selectAct = (a) => setPrefill({ mensaje: `Me interesa el Acto ${a.n} · ${a.title}` });
  return (
    <>
      <PageHeader
        eyebrow="El Nuevo Consumidor · B2B · en Grupo Hiperconvercloud."
        eyebrowColor="#81D4FA"
        title={["Tu producto, viaja con ellas", "se parte de la historia."]}
        subtitle="Las audiencias jóvenes —Millennials y Gen Z— esquivan activamente los anuncios tradicionales. Hacen scroll, cambian de pantalla o simplemente los ignoran. Para que una marca realmente conecte hoy, debe convertirse en un personaje más de la historia."
      />

      <section className="px-5 sm:px-8 lg:px-12 pt-4 pb-6">
        <div className="mx-auto max-w-7xl">
          <Reveal className="relative overflow-hidden rounded-[2.5rem] shadow-[0_20px_40px_rgb(0,0,0,0.08)]">
            <img
              src={IMG.duoBrandProducts}
              alt="Anto y Bella felices mostrando productos de marca"
              className="w-full object-cover"
              data-testid="brand-products-image"
            />
            <span className="absolute left-5 top-5 rounded-full bg-sky px-4 py-1.5 font-mono text-[11px] font-bold uppercase tracking-widest text-ink">
              Los comerciales ya no funcionan. El Nuevo Consumidor
            </span>
          </Reveal>
          <h2 className="mt-4 font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-ink">
            Así se ve tu marca
          </h2>
          <p className="mt-4 max-w-2xl text-ink/60 leading-relaxed">
           La nueva era del marketing de viaje: integración orgánica en 6 actos neuroemocionales, activación en tiempo real a través de la App de doble pantalla, y conversión directa al e-commerce de tu marca.
          </p>
        </div>
      </section>

      <Section className="!pt-6">
        <Reveal className="mb-10">
          <Eyebrow color="#81D4FA">Cómo participa tu marca</Eyebrow>
          <h2 className="mt-4 font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-ink">
            Seis actos. Seis disparadores biológicos
          </h2>
          <p className="mt-4 max-w-2xl text-ink/60 leading-relaxed">
           Cada episodio está diseñado con ingeniería emocional para maximizar la retención y la recordación. Tu integración ocurre dentro del estado emocional preciso donde el espectador está más receptivo a tu categoría de producto.
          </p>
        </Reveal>
        <ActLayers acts={BRAND_ACTS} onSelect={selectAct} />
      </Section>

      <Section alt className="!py-12">
        <CommercialInventory audience="brand" />
      </Section>

      <Section>
        <Reveal className="mb-10">
          <Eyebrow color="#81D4FA">ROI por episodio</Eyebrow>
          <h2 className="mt-4 font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-ink">
            Lo que tu marca puede lograr
          </h2>
          <p className="mt-4 max-w-2xl text-ink/60 leading-relaxed">
            Siete ejemplos de retorno alcanzable en un solo episodio, combinando TV abierta y las
            redes sociales del programa. Son cifras estimadas para que dimensiones el impacto.
          </p>
          <p className="mt-4 max-w-2xl text-ink/60 leading-relaxed">
           La App de segunda pantalla convierte cada aparición de tu producto en televisión abierta en una experiencia interactiva y en una oportunidad de compra directa, en tiempo real.
          </p>
        </Reveal>
        <RoiGrid items={BRAND_ROI} accent="#81D4FA" />
      </Section>

      <Section alt>
        <Reveal className="mb-12 text-center">
          <Eyebrow color="#81D4FA" className="justify-center">
            Paquetes de marca
          </Eyebrow>
          <h2 className="mx-auto mt-4 max-w-2xl font-display text-4xl sm:text-5xl font-extrabold leading-none tracking-tighter text-ink">
            Inversión por episodio
          </h2>
          <p className="mx-auto mt-4 max-w-lg text-ink/60">
            Cuatro niveles para cubrir uno, dos o los seis actos del episodio, con
            product placement adaptable a tu objetivo.
          </p>
        </Reveal>
        <PriceCards tiers={BRAND_TIERS} accent="#81D4FA" />
      </Section>

      <DuoBanner
        image={IMG.duoBiz}
        imgSide="left"
        tint="#81D4FA"
        eyebrowColor="#81D4FA"
        title="TV en el sala. Conversión en el celular."
        text="Los paquetes de integración están diseñados para marcas que buscan presencia narrativa orgánica, sin exclusividad de show ni naming del episodio. Máximo 3 marcas por episodio para garantizar la escasez y el valor de tu integración. La App de segunda pantalla convierte cada aparición de tu producto en televisión abierta en una experiencia interactiva y en una oportunidad de compra directa, en tiempo real."

      />

      <OnboardingCTA
        to="/marcas/onboarding"
        accent="#81D4FA"
        title="Responde 10 preguntas y recibe tu propuesta"
        text="Un cuestionario corto para entender tu producto y armarte una propuesta de integración clara en 48 horas."
      />

      <Section id="contacto">
        <div className="grid gap-12 lg:grid-cols-[1fr_1fr]">
          <Reveal>
            <Eyebrow color="#FF8A80">Hablemos</Eyebrow>
            <h2 className="mt-4 font-display text-4xl sm:text-5xl font-extrabold leading-none tracking-tighter text-ink">
              Encontremos tu módulo ideal.
            </h2>
            <p className="mt-6 text-lg text-ink/70 leading-relaxed">
              Anto & Bella por el Mundo™ ofrece competitividad superior frente a medios publicitarios aislados, garantizando un impacto digital acumulado en un entorno de prestigio televisivo. Dinos qué producto quieres integrar y en qué destinos. Te armamos
              una propuesta clara con opciones y precios.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="rounded-[2rem] bg-white p-8 shadow-[0_20px_40px_rgb(0,0,0,0.08)]">
              <ContactForm
                testidPrefix="brand"
                sourcePath="/marcas"
                accent="#81D4FA"
                prefill={prefill}
                submitLabel="Solicitar propuesta"
                successMsg="¡Gracias! Te contactamos en 48h."
                fields={[
                  { name: "empresa", label: "Marca", placeholder: "Nombre de tu marca" },
                  { name: "nombre", label: "Contacto", placeholder: "Tu nombre" },
                  { name: "email", label: "Email corporativo", type: "email", placeholder: "nombre@empresa.com" },
                  { name: "mensaje", label: "Producto a integrar", type: "textarea", placeholder: "¿Qué te gustaría integrar?", required: false },
                ]}
              />
            </div>
          </Reveal>
        </div>
      </Section>
    </>
  );
}
